import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Input,
  Radio,
  Select,
  Spin,
  Table,
  Typography,
} from 'antd';
import {
  ClearOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TableProps } from 'antd/lib/table';
import { useMedia } from 'react-use';

import { SortDirection } from 'api/graphql-global-types';
import { fetchInterviewChatSummary } from 'api/interviewChats/client';
import { queryInterviewChannels } from 'api/interviewChats/stream';
import {
  ChatChannelState,
  InterviewChatChannelFilter,
  InterviewChatGranularity,
  InterviewChatOrderBy,
  InterviewChatRow,
  InterviewChatSummary,
} from 'api/interviewChats/types';

import { useDebouncedValue, useStreamChatClient } from 'hooks';
import {
  DEFAULT_PAGE_SIZE,
  DateRange,
  EMPTY_SUMMARY,
  NEW_CHATS_STROKE,
  SEARCH_DEBOUNCE_MS,
  SPLIT_VIEW_MIN_WIDTH,
  STATE_OPTIONS,
  formatDateTime,
  onlyIfCurrent,
  rollUpNewChats,
  runLoad,
} from 'helpers/interviewChats';

import PageToolbar from 'ui/PageToolbar/PageToolbar';

import ChatTranscript from './components/ChatTranscript/ChatTranscript';
import FunnelStrip from './components/FunnelStrip/FunnelStrip';
import styles from './InterviewChats.module.scss';
import { SORT_KEY_TO_ORDER_BY, buildChannelColumns } from './channelColumns';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Two independent sources, on purpose.
 *
 * The funnel is one unfiltered fs-backend call made when the page loads: it describes every
 * interview chat, so it stays a stable denominator no matter what you type below.
 *
 * The list is GetStream's own `queryChannels` — searching, sorting and paging all happen
 * inside GetStream, one page at a time. Neither the API pod nor this page ever holds more
 * than 30 chats, so the cost does not grow with how many chats exist.
 */
const InterviewChats: React.FC = () => {
  const apolloClient = useApolloClient();
  const isSplitView = useMedia(`(min-width: ${SPLIT_VIEW_MIN_WIDTH}px)`);
  const {
    client: streamClient,
    loading: streamLoading,
    error: streamError,
  } = useStreamChatClient();

  const [searchInput, setSearchInput] = useState('');
  const [range, setRange] = useState<DateRange>(null);
  const [states, setStates] = useState<ChatChannelState[]>([]);
  const [granularity, setGranularity] =
    useState<InterviewChatGranularity>('Day');
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState<InterviewChatOrderBy>('LastMessageAt');
  const [direction, setDirection] = useState<SortDirection>(SortDirection.DESC);

  const [summary, setSummary] = useState<InterviewChatSummary>(EMPTY_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [rows, setRows] = useState<InterviewChatRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [atOffsetCeiling, setAtOffsetCeiling] = useState(false);
  const [searchCapped, setSearchCapped] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedCid, setSelectedCid] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // Sorted states keep the filter identity stable when the tag order changes
  // without changing the selection.
  const statesKey = useMemo(() => [...states].sort().join(','), [states]);

  /**
   * Everything here maps onto a GetStream filter, so changing any of it re-queries rather
   * than narrowing something already in memory.
   *
   * TZ-safe: start-of-day for fromDate and end-of-day for toDate so a picked calendar date
   * isn't shifted across timezone boundaries.
   */
  const filter = useMemo<InterviewChatChannelFilter>(() => {
    const next: InterviewChatChannelFilter = {};

    if (debouncedSearch.trim()) next.search = debouncedSearch.trim();

    const fromDate = range?.[0];
    if (fromDate) next.fromDate = fromDate.clone().startOf('day').toISOString();

    const toDate = range?.[1];
    if (toDate) next.toDate = toDate.clone().endOf('day').toISOString();

    // GetStream exposes the lifecycle as one boolean, so picking both states says nothing.
    if (states.length === 1) next.frozen = states[0] === 'Frozen';

    return next;
    // statesKey instead of the array so reordering the same members doesn't re-query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, range, statesKey]);

  // A new filter or a new sort means page one. Done during render rather than in an effect
  // so the table never asks GetStream for an offset the new filter has no rows at.
  const [prevView, setPrevView] = useState({ filter, orderBy, direction });
  if (
    prevView.filter !== filter ||
    prevView.orderBy !== orderBy ||
    prevView.direction !== direction
  ) {
    setPrevView({ filter, orderBy, direction });
    setPage(1);
  }

  // Only the newest request may write to state — an earlier, slower response must not
  // overwrite the current page (or hide its spinner). One counter each: the two calls are
  // independent, so a re-query of the list must not invalidate an in-flight summary.
  const summaryRequestId = useRef(0);
  const rowsRequestId = useRef(0);

  const loadSummary = useCallback(() => {
    const id = (summaryRequestId.current += 1);
    const isCurrent = () => id === summaryRequestId.current;

    return runLoad(
      onlyIfCurrent(setSummaryLoading, isCurrent),
      onlyIfCurrent(setError, isCurrent),
      'Failed to load interview chat adoption.',
      async () => {
        const data = await fetchInterviewChatSummary(apolloClient);
        if (isCurrent()) setSummary(data);
      }
    );
  }, [apolloClient]);

  const loadRows = useCallback(
    (
      currentFilter: InterviewChatChannelFilter,
      currentPage: number,
      currentOrderBy: InterviewChatOrderBy,
      currentDirection: SortDirection
    ) => {
      if (!streamClient) return undefined;

      const id = (rowsRequestId.current += 1);
      const isCurrent = () => id === rowsRequestId.current;

      return runLoad(
        onlyIfCurrent(setRowsLoading, isCurrent),
        onlyIfCurrent(setError, isCurrent),
        'Failed to load interview chats.',
        async () => {
          const result = await queryInterviewChannels(streamClient, {
            filter: currentFilter,
            orderBy: currentOrderBy,
            ascending: currentDirection === SortDirection.ASC,
            limit: DEFAULT_PAGE_SIZE,
            offset: (currentPage - 1) * DEFAULT_PAGE_SIZE,
          });

          if (!isCurrent()) return;

          setRows(result.rows);
          setHasMore(result.hasMore);
          setAtOffsetCeiling(result.atOffsetCeiling);
          setSearchCapped(result.searchCapped);
        }
      );
    },
    [streamClient]
  );

  // Once, when the page loads. The funnel does not react to the filters below it.
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadRows(filter, page, orderBy, direction);
  }, [loadRows, filter, page, orderBy, direction]);

  const handleRefresh = useCallback(() => {
    setError(null);
    loadSummary();
    loadRows(filter, page, orderBy, direction);
  }, [loadSummary, loadRows, filter, page, orderBy, direction]);

  const handleClear = useCallback(() => {
    setSearchInput('');
    setRange(null);
    setStates([]);
    setGranularity('Day');
  }, []);

  const filtersDirty =
    searchInput.length > 0 ||
    !!range?.[0] ||
    !!range?.[1] ||
    states.length > 0 ||
    granularity !== 'Day';

  // Selection has to survive paging, and the selected chat may not be on the page any more.
  const [selectedChat, setSelectedChat] = useState<InterviewChatRow | null>(
    null
  );

  const handleSelectChat = useCallback(
    (row: InterviewChatRow) => {
      setSelectedCid(row.providerChannelCid);
      setSelectedChat(row);
      if (!isSplitView) setDrawerOpen(true);
    },
    [isSplitView]
  );

  const chartData = useMemo(
    () => rollUpNewChats(summary.newChatsByDay, granularity),
    [summary.newChatsByDay, granularity]
  );

  const columns = useMemo(
    () => buildChannelColumns(orderBy, direction),
    [orderBy, direction]
  );

  // Sorting only: paging is the Previous/Next pager below the table. Clearing a column
  // falls back to the default order rather than leaving the list unsorted. GetStream sorts
  // across the whole result, not just the visible page.
  const handleTableChange = useCallback<
    NonNullable<TableProps<InterviewChatRow>['onChange']>
  >((_pagination, _filters, sorter) => {
    // Only one column is sortable at a time, so the array form never carries more.
    const active = Array.isArray(sorter) ? sorter[0] : sorter;

    setOrderBy(
      SORT_KEY_TO_ORDER_BY[String(active?.columnKey ?? '')] ?? 'LastMessageAt'
    );
    setDirection(
      active?.order === 'ascend' ? SortDirection.ASC : SortDirection.DESC
    );
  }, []);

  const loading = rowsLoading || streamLoading;

  // GetStream returns no total, so there is nothing honest to number pages with: the
  // pager is Previous/Next, and stays hidden while everything fits on page one.
  const showPager = page > 1 || hasMore;

  const transcript = (
    <ChatTranscript chat={selectedChat} inDrawer={!isSplitView} />
  );

  return (
    <div className={styles.container}>
      <PageToolbar
        leading={
          <Text type="secondary">
            Adoption and usage of interview host↔guest chats, plus the
            conversations themselves.
          </Text>
        }
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Host or guest name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e?.target?.value ?? '')}
          allowClear
          style={{ width: 220, maxWidth: '100%' }}
        />
        <RangePicker
          value={range as any}
          onChange={(next) => setRange(next as DateRange)}
          allowClear
        />
        <Select<ChatChannelState[]>
          mode="multiple"
          placeholder="State"
          value={states}
          onChange={(next) => setStates(next ?? [])}
          options={STATE_OPTIONS}
          style={{ minWidth: 180 }}
          allowClear
          maxTagCount="responsive"
        />
        <Button
          icon={<ClearOutlined />}
          onClick={handleClear}
          disabled={!filtersDirty}
        >
          Clear
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </PageToolbar>

      {(error || streamError) && (
        <Alert
          type="error"
          message={error || streamError}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {summary.scanFailed ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not finish reading chats from the chat provider — treat the adoption numbers as unreliable, not just partial."
          description="Retry with Refresh. If it keeps failing, the GetStream integration needs looking at."
        />
      ) : (
        summary.truncated && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="More interview chats exist than a single scan covers — the adoption numbers describe the most recently created ones. The list below is unaffected."
          />
        )
      )}

      <FunnelStrip summary={summary} loading={summaryLoading} />

      <div
        className={[styles.inbox, isSplitView ? '' : styles.inboxSingle]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles.listColumn}>
          <div className={styles.listMeta}>
            <span>Searching, sorting and paging run in GetStream.</span>
            <span>
              {summary.generatedAt &&
                `Adoption as of ${formatDateTime(summary.generatedAt)}`}
            </span>
          </div>

          {atOffsetCeiling && (
            <Alert
              type="warning"
              showIcon
              banner
              message="GetStream stops paging at 1000 chats — narrow the search or the date range to reach older ones."
            />
          )}

          {searchCapped && (
            <Alert
              type="warning"
              showIcon
              banner
              message="Over 100 people match this name, so some chats may be missing — type more of the name."
            />
          )}

          <div className={styles.tableWrap}>
            <Table<InterviewChatRow>
              rowKey={(row) => row.providerChannelCid}
              columns={columns}
              dataSource={rows}
              loading={loading}
              size="small"
              locale={{ emptyText: 'No chats match the current filters.' }}
              onChange={handleTableChange}
              onRow={(row) => ({
                onClick: () => handleSelectChat(row),
                className: [
                  styles.clickableRow,
                  row.providerChannelCid === selectedCid
                    ? styles.selectedRow
                    : '',
                ]
                  .filter(Boolean)
                  .join(' '),
              })}
              pagination={false}
            />
          </div>

          {showPager && (
            <div className={styles.pager}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {hasMore ? 'More chats available' : 'End of the list'}
              </Text>
              <div className={styles.pagerControls}>
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  disabled={page === 1 || loading}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <Text>Page {page}</Text>
                <Button
                  size="small"
                  disabled={!hasMore || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                  <RightOutlined />
                </Button>
              </div>
            </div>
          )}
        </div>

        {isSplitView && transcript}
      </div>

      <div className={`${styles.card} ${styles.chartCard}`}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>New chats over time</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Chats bucket by creation date. Buckets are UTC
              {granularity === 'Week' ? ', weeks start Monday' : ''}.
            </Text>
          </div>
          <Radio.Group
            value={granularity}
            onChange={(e) =>
              setGranularity(e.target.value as InterviewChatGranularity)
            }
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="Day">Day</Radio.Button>
            <Radio.Button value="Week">Week</Radio.Button>
          </Radio.Group>
        </div>
        <div className={styles.chartBody}>
          {summaryLoading ? (
            <div className={styles.chartLoading}>
              <Spin />
            </div>
          ) : chartData.length === 0 ? (
            <div className={styles.chartEmpty}>
              <Text type="secondary">No interview chats yet.</Text>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="New chats"
                  stroke={NEW_CHATS_STROKE}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {!isSplitView && (
        <Drawer
          visible={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="right"
          width="min(560px, 100vw)"
          bodyStyle={{ padding: 0, height: '100%' }}
          title="Conversation"
          destroyOnClose
        >
          {transcript}
        </Drawer>
      )}
    </div>
  );
};

export default InterviewChats;
